'use client';
// this whole meta pixel topic had very bad documentation
// this is heavily AI coded
import { useEffect, useRef } from 'react';
import { useCookieConsent } from '@/app/context/cookieConsentContext';
import { processMetaPixelQueue } from '@/app/utils/metaPixel';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

export default function MetaPixelProvider() {
  const { consentGiven, isMounted } = useCookieConsent();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const attemptsRef = useRef(0);
  const maxAttempts = 50; // 5 seconds max (50 * 100ms)

  useEffect(() => {
    // Cleanup function to clear any existing timeout
    const cleanup = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      attemptsRef.current = 0;
    };

    if (!isMounted || typeof window === 'undefined') {
      cleanup();
      return;
    }

    if (consentGiven === 'accepted') {
      if (!process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID) {
        // Pixel ID not configured, skip initialization
        cleanup();
        return;
      }

      // Check if Meta Pixel script is already loaded
      if (window.fbq) {
        // Script is already loaded, just initialize
        window.fbq('init', process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID);
        window.fbq('track', 'PageView');
        // Process any queued events now that Meta Pixel is ready
        processMetaPixelQueue();
        cleanup();
      } else {
        // Check if script element already exists to avoid duplicates
        const existingScript = document.getElementById('meta-pixel-js');
        if (existingScript) {
          // Check if fbq is already available from the existing script
          if (window.fbq && typeof window.fbq === 'function') {
            const fbq = window.fbq as (...args: unknown[]) => void;
            fbq('init', process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID);
            fbq('track', 'PageView');
            // Process any queued events now that Meta Pixel is ready
            processMetaPixelQueue();
            cleanup();
            return;
          }

          // Monitor the existing script for when fbq becomes available
          const monitorExistingScript = () => {
            if (window.fbq && typeof window.fbq === 'function') {
              (window.fbq as (...args: unknown[]) => void)(
                'init',
                process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID,
              );
              (window.fbq as (...args: unknown[]) => void)('track', 'PageView');
              // Process any queued events now that Meta Pixel is ready
              processMetaPixelQueue();
              cleanup();
            } else {
              attemptsRef.current++;
              if (attemptsRef.current >= maxAttempts) {
                cleanup();
                return;
              }
              timeoutRef.current = setTimeout(monitorExistingScript, 100);
            }
          };

          monitorExistingScript();
          return;
        }

        // Script is not loaded, load it dynamically
        const loadMetaPixelScript = () => {
          const script = document.createElement('script');
          script.id = 'meta-pixel-js';
          script.innerHTML = `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
          `;
          document.head.appendChild(script);

          // Wait for script to load and then initialize
          const waitForMetaPixel = () => {
            if (window.fbq) {
              window.fbq('init', process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID);
              window.fbq('track', 'PageView');
              // Process any queued events now that Meta Pixel is ready
              processMetaPixelQueue();
              cleanup(); // Clear timeout on success
            } else {
              attemptsRef.current++;
              if (attemptsRef.current >= maxAttempts) {
                cleanup();
                return;
              }
              timeoutRef.current = setTimeout(waitForMetaPixel, 100);
            }
          };
          waitForMetaPixel();
        };

        loadMetaPixelScript();
      }
    } else {
      // Consent not accepted, cleanup any existing timeouts
      cleanup();
    }

    // Return cleanup function for useEffect
    return cleanup;
  }, [consentGiven, isMounted]);

  // Don't render anything - this component only handles initialization
  return null;
}
